import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scroll behavior on route change:
 *  - No hash → jump to the top (standard SPA nav; a new page starts at the top).
 *  - Hash (e.g. /#passes) → smooth-scroll that element into view.
 *
 * Keyed on the full location (pathname + hash) so it also fires when only the
 * hash changes while already on the same page — e.g. clicking "Passes" in the
 * footer while sitting on the home page. React Router's <Link> does NOT scroll
 * to a hash on its own, so this is where that happens.
 *
 * The target section may not be painted yet on a cross-page jump
 * (/privacy → /#passes mounts the home page first), so we retry on the next
 * few frames until the element exists.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const id = hash.slice(1);
    let frames = 0;
    let raf = 0;

    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      // element not mounted yet — retry for up to ~20 frames (~330ms)
      if (frames++ < 20) {
        raf = requestAnimationFrame(tryScroll);
      }
    };

    raf = requestAnimationFrame(tryScroll);
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);

  return null;
}
